import { useState, useEffect } from 'react';
import { X, AlertTriangle, Clock, User, Shield, FileText, ChevronDown, ChevronUp, Loader2, Mail, UserCheck } from 'lucide-react';
import { alertService, type AlertDetail } from '@/services/alertService';

interface AlertDetailModalProps {
  alertId: string;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: () => void;
}

const severityColors: Record<string, string> = {
  low: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  medium: 'bg-orange-100 text-orange-800 border-orange-300',
  high: 'bg-red-100 text-red-800 border-red-300',
  critical: 'bg-red-200 text-red-900 border-red-500',
};

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  acknowledged: 'bg-purple-100 text-purple-800',
  investigating: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  escalated: 'bg-red-100 text-red-800',
  dismissed: 'bg-gray-100 text-gray-800',
};

export function AlertDetailModal({ alertId, isOpen, onClose, onStatusChange }: AlertDetailModalProps) {
  const [alert, setAlert] = useState<AlertDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && alertId) {
      fetchAlertDetail();
    }
  }, [isOpen, alertId]);

  const fetchAlertDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await alertService.getAlertDetail(alertId);
      setAlert(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load alert details');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: string) => {
    if (action === 'resolved' || action === 'escalated' || action === 'dismissed') {
      setPendingAction(action);
      setShowNotes(true);
      return;
    }

    await executeAction(action);
  };

  const executeAction = async (action: string, actionNotes?: string) => {
    try {
      setActionLoading(action);
      setError(null);
      await alertService.updateAlertStatus(alertId, action, actionNotes || notes || undefined);
      await fetchAlertDetail();
      setNotes('');
      setShowNotes(false);
      setPendingAction(null);
      onStatusChange();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update alert');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmAction = () => {
    if (pendingAction) {
      executeAction(pendingAction, notes);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const formatTimeAgo = (dateStr: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <h2 className="text-xl font-bold text-gray-900">Alert Details</h2>
          <button onClick={onClose} className="btn-glass p-1.5 rounded-full">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            <span className="ml-3 text-gray-600">Loading alert details...</span>
          </div>
        ) : error && !alert ? (
          <div className="p-6 text-center text-red-600">{error}</div>
        ) : alert ? (
          <div className="p-6 space-y-6">
            {/* Error banner */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Alert Summary */}
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full ${alert.severity === 'high' || alert.severity === 'critical' ? 'bg-red-100' : 'bg-orange-100'}`}>
                <AlertTriangle size={24} className={alert.severity === 'high' || alert.severity === 'critical' ? 'text-red-600' : 'text-orange-600'} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{alert.title}</h3>
                <p className="text-gray-600 mt-1">{alert.description}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${severityColors[alert.severity] || severityColors.low}`}>
                    {alert.severity.toUpperCase()}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[alert.status] || statusColors.new}`}>
                    {alert.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {alert.type.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            {/* Student Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <User size={18} className="text-gray-500" />
                <h4 className="font-semibold text-gray-800">Student Information</h4>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-2 font-medium">{alert.student.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>
                  <span className="ml-2 font-medium">{alert.student.email}</span>
                </div>
                <div>
                  <span className="text-gray-500">School:</span>
                  <span className="ml-2 font-medium">{alert.school.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Detected:</span>
                  <span className="ml-2 font-medium">{formatDate(alert.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* AI Detection Details */}
            {alert.mlResult && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Shield size={18} className="text-blue-500" />
                  <h4 className="font-semibold text-gray-800">AI Detection Details</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <span className="text-gray-500">Detection Type:</span>
                    <span className="ml-2 font-medium capitalize">{alert.mlResult.detectionType.replace(/_/g, ' ')}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Severity:</span>
                    <span className="ml-2 font-medium capitalize">{alert.mlResult.severity}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Model:</span>
                    <span className="ml-2 font-medium">{alert.mlResult.modelVersion}</span>
                  </div>
                </div>
                {/* Visual confidence gauge */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">AI Confidence</span>
                    <span className={`text-xs font-bold ${
                      alert.mlResult.confidence >= 0.85 ? 'text-red-600' :
                      alert.mlResult.confidence >= 0.6 ? 'text-orange-600' : 'text-yellow-600'
                    }`}>{Math.round(alert.mlResult.confidence * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-2.5 rounded-full transition-all ${
                        alert.mlResult.confidence >= 0.85 ? 'bg-red-500' :
                        alert.mlResult.confidence >= 0.6 ? 'bg-orange-400' : 'bg-yellow-400'
                      }`}
                      style={{ width: `${Math.round(alert.mlResult.confidence * 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                    <span>Low</span><span>Medium</span><span>High</span>
                  </div>
                </div>
                {alert.mlResult.inputText && (
                  <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                    <span className="text-xs text-gray-500 block mb-1">Flagged Content:</span>
                    <p className="text-sm text-gray-800 italic">"{alert.mlResult.inputText}"</p>
                  </div>
                )}
              </div>
            )}

            {/* Recent Student Activity */}
            {alert.student.recentActivity.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <FileText size={18} className="text-gray-500" />
                  <h4 className="font-semibold text-gray-800">Recent Browser Activity</h4>
                </div>
                <div className="space-y-2">
                  {alert.student.recentActivity.map((event) => (
                    <div key={event.id} className={`p-3 rounded border text-sm ${event.isFlagged ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {event.content && <p className="text-gray-800">{event.content.substring(0, 150)}{event.content.length > 150 ? '...' : ''}</p>}
                          {event.url && <p className="text-xs text-gray-500 mt-1">{event.domain || event.url}</p>}
                          {event.isFlagged && event.flagReason && (
                            <p className="text-xs text-red-600 mt-1">⚠ {event.flagReason}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 ml-3 whitespace-nowrap">{formatTimeAgo(event.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Alert History */}
            {alert.history.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="btn-glass w-full flex items-center gap-3 text-left"
                  style={{ justifyContent: 'flex-start' }}
                >
                  <Clock size={16} className="text-slate-500 shrink-0" />
                  <span className="font-semibold text-slate-700 flex-1">
                    Alert Timeline ({alert.history.length})
                  </span>
                  {showHistory ? <ChevronUp size={16} className="text-slate-400 shrink-0" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" />}
                </button>
                {showHistory && (
                  <div className="mt-3 space-y-3">
                    {alert.history.map((entry) => (
                      <div key={entry.id} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                        <div>
                          <p className="text-gray-800">
                            <span className="font-medium">{entry.changedBy}</span> changed status
                            {entry.previousStatus && <> from <span className="font-medium">{entry.previousStatus}</span></>}
                            {' '}to <span className="font-medium">{entry.newStatus}</span>
                          </p>
                          {entry.notes && <p className="text-gray-600 mt-1">Note: {entry.notes}</p>}
                          <p className="text-xs text-gray-400 mt-1">{formatDate(entry.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Resolution Notes */}
            {alert.resolutionNotes && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Resolution Notes</h4>
                <p className="text-sm text-gray-700">{alert.resolutionNotes}</p>
                {alert.resolvedBy && (
                  <p className="text-xs text-gray-500 mt-2">
                    Resolved by {alert.resolvedBy.name} on {alert.resolvedAt ? formatDate(alert.resolvedAt) : 'N/A'}
                  </p>
                )}
              </div>
            )}

            {/* Notes Input (for resolve/escalate/dismiss) */}
            {showNotes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">
                  {pendingAction === 'resolved' ? 'Resolution Notes' :
                   pendingAction === 'escalated' ? 'Escalation Reason' :
                   'Dismissal Reason'}
                </h4>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    pendingAction === 'resolved' ? 'Describe how this was resolved...' :
                    pendingAction === 'escalated' ? 'Why is this being escalated...' :
                    'Why is this being dismissed...'
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={confirmAction}
                    disabled={!!actionLoading}
                    className={`btn-glass disabled:opacity-50 ${
                      pendingAction === 'resolved' ? 'btn-glass-emerald' :
                      pendingAction === 'escalated' ? 'btn-glass-danger' :
                      'btn-glass-purple'
                    }`}
                  >
                    {actionLoading ? <Loader2 className="animate-spin h-4 w-4" /> : `Confirm ${pendingAction}`}
                  </button>
                  <button
                    onClick={() => { setShowNotes(false); setPendingAction(null); setNotes(''); }}
                    className="btn-glass"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!['resolved', 'dismissed'].includes(alert.status) && !showNotes && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                {/* Status actions */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status Actions</h4>
                  <div className="flex flex-wrap gap-2">
                    {alert.status === 'new' && (
                      <button
                        onClick={() => handleAction('acknowledged')}
                        disabled={!!actionLoading}
                        className="btn-glass btn-glass-purple disabled:opacity-50"
                      >
                        {actionLoading === 'acknowledged' ? <Loader2 className="animate-spin h-4 w-4 inline mr-1" /> : null}
                        Acknowledge
                      </button>
                    )}
                    {['new', 'acknowledged'].includes(alert.status) && (
                      <button
                        onClick={() => handleAction('investigating')}
                        disabled={!!actionLoading}
                        className="btn-glass btn-glass-amber disabled:opacity-50"
                      >
                        {actionLoading === 'investigating' ? <Loader2 className="animate-spin h-4 w-4 inline mr-1" /> : null}
                        Investigate
                      </button>
                    )}
                    <button
                      onClick={() => handleAction('resolved')}
                      disabled={!!actionLoading}
                      className="btn-glass btn-glass-emerald disabled:opacity-50"
                    >
                      Resolve
                    </button>
                    <button
                      onClick={() => handleAction('escalated')}
                      disabled={!!actionLoading}
                      className="btn-glass btn-glass-danger disabled:opacity-50"
                    >
                      Escalate to Admin
                    </button>
                    <button
                      onClick={() => handleAction('dismissed')}
                      disabled={!!actionLoading}
                      className="btn-glass disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
                {/* Quick contact actions */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Quick Contact</h4>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`mailto:?subject=${encodeURIComponent('Regarding Safety Alert')}&body=${encodeURIComponent('Dear Parent/Guardian,\n\nWe wanted to bring to your attention a safety alert involving your child.\n\nPlease contact us to discuss this matter.\n\nThank you.')}`}
                      className="btn-glass btn-glass-blue"
                    >
                      <Mail className="h-4 w-4" />
                      Contact Parent
                    </a>
                    <button
                      onClick={() => {
                        setPendingAction('escalated');
                        setNotes('Assigned to school counselor for follow-up.');
                        setShowNotes(true);
                      }}
                      disabled={!!actionLoading}
                      className="btn-glass btn-glass-teal disabled:opacity-50"
                    >
                      <UserCheck className="h-4 w-4" />
                      Assign to Counselor
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Assigned To */}
            {alert.assignedTo && (
              <div className="text-sm text-gray-500">
                Assigned to: <span className="font-medium text-gray-700">{alert.assignedTo.name}</span>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
